// DealTradesCard — the job's trades on the deal page, next to Ops Requests (Oct 28, 2026).
//
// "+ Add trade" for Alex, Robert and leadership; the visits on this job with everything that was agreed;
// done and cancelled ones fold into a count. An RM sees their job's trades without prices (RLS). Renders
// nothing for someone who can't add a trade when the job has none.
//
// Oct 29: /deal/<id>?trade=<trade id> (the link ho-portal-notify texts whoever set a trade up when the homeowner
// signs its add-on: "Open it and click Schedule") scrolls that trade's card into view and, when its add-on is signed
// and a visit of the job has no day yet, opens Move on it. The param is cleared once used.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { etDate } from "../../lib/weeklySpiffs";
import {
  canMarkTradeDone, canTrade, fetchDealTrades, fetchTradePrices, isOpenTrade, priceFor, type Trade, type TradePrice,
} from "../../lib/trades";
import TradeCard from "./TradeCard";
import { useStaffNames, useTradeModals } from "./useTradeModals";

export default function DealTradesCard({ dealId }: { dealId: string }) {
  const { role, isSuperUser } = useAuth();
  const canAct = canTrade(role, isSuperUser);
  const canDone = canMarkTradeDone(role, isSuperUser);
  const [trades, setTrades] = useState<Trade[] | null>(null);
  const [prices, setPrices] = useState<Record<string, TradePrice>>({});
  const [showClosed, setShowClosed] = useState(false);
  const names = useStaffNames();
  const today = useMemo(() => etDate(new Date()), []);

  const load = useCallback(async () => {
    try {
      const rows = await fetchDealTrades(dealId);
      // The rows and their prices land together, so ?trade= below reads the add-on with its trade.
      const pr = await fetchTradePrices(rows.map((t) => t.id)).catch(() => ({}));
      setTrades(rows);
      setPrices(pr);
    } catch {
      setTrades((t) => t ?? []); // additive card: a failed read just shows the button
    }
  }, [dealId]);
  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("trade_items", () => { void load(); });
  const modals = useTradeModals(today, () => void load());

  // ?trade=<id>: that trade in view; Move opened when its add-on is signed and the job has a visit with no day.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get("trade");
  const [focused, setFocused] = useState<string | null>(null);
  const { openAction } = modals;
  useEffect(() => {
    if (!focusParam || !trades) return;
    const next = new URLSearchParams(window.location.search);
    next.delete("trade");
    setSearchParams(next, { replace: true });
    const t = trades.find((x) => x.id === focusParam);
    if (!t) return;
    setFocused(t.id);
    if (!isOpenTrade(t)) setShowClosed(true);
    window.setTimeout(() => document.getElementById(`trade-${t.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    const job = priceFor(t, trades, prices)?.price ?? null;
    const visits = [t, ...(t.pair_id ? trades.filter((o) => o.pair_id === t.pair_id && o.id !== t.id) : [])];
    const noDay = visits.find((v) => isOpenTrade(v) && !v.scheduled_date);
    if (canAct && job?.addendum_status === "signed" && noDay) openAction(noDay, "move");
  }, [focusParam, trades, prices, canAct, openAction, setSearchParams]);

  if (!trades) return null;
  if (!canAct && trades.length === 0) return null;
  const open = trades.filter(isOpenTrade);
  const closed = trades.filter((t) => !isOpenTrade(t));
  const partnerOf = (t: Trade) => (t.pair_id ? trades.find((o) => o.pair_id === t.pair_id && o.id !== t.id) ?? null : null);

  const card = (t: Trade) => (
    <TradeCard
      key={t.id}
      t={t}
      focused={focused === t.id}
      partner={partnerOf(t)}
      price={priceFor(t, trades, prices)}
      today={today}
      names={names}
      canAct={canAct}
      canDone={canDone}
      onEdit={(x) => modals.openEdit(x, priceFor(x, trades, prices)?.price ?? null)}
      onAction={modals.openAction}
      onChanged={() => void load()}
    />
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-bold text-bardin-ink text-sm">
          🔧 Trades
          {open.length > 0 && <span className="ml-2 text-xs font-semibold text-slate-500">{open.length} open</span>}
        </h3>
        <div className="flex items-center gap-3">
          <Link to="/build-calendar?view=trades" className="text-xs text-bardin-teal font-semibold underline">Builds board →</Link>
          {canAct && (
            <button type="button" onClick={() => modals.openNew(dealId)} className="px-3 py-1.5 rounded-lg bg-bardin-orange text-white text-xs font-semibold shadow-sm">
              + Add trade
            </button>
          )}
        </div>
      </div>
      {open.length === 0 && (
        <p className="text-xs text-slate-500">
          {canAct ? "Agreed something with JAC or another sub (gutters, guards, soffit and fascia, a detach and reset)? Put it here the same day: it goes on the Other Trades calendar with everything that was said." : "No open trades on this job."}
        </p>
      )}
      {open.map(card)}
      {closed.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowClosed((v) => !v)} className="text-xs text-slate-500 underline">
            {showClosed ? "Hide" : "Show"} {closed.length} done or cancelled
          </button>
          {showClosed && <div className="mt-2 space-y-2">{closed.map(card)}</div>}
        </div>
      )}
      {modals.element}
    </div>
  );
}
